// BSIResultsDisplay — Dashboard moderne des résultats BSI
// KPIs en haut, timeline des actes, explications verticales, règles en badges

import { useState } from 'react';
import type { BSIResult, RegleAppliquee } from '../intelligence/types';
import type { ExplicationBSI } from '../intelligence/explanationEngine';
import { genererPourquoi } from '../intelligence/explanationEngine';
import NGAPRulesPanel from './NGAPRulesPanel';

interface BSIResultsDisplayProps {
  bsiResult: BSIResult;
  explication: ExplicationBSI;
  regles: RegleAppliquee[];
  mode: 'coter' | 'expliquer';
}

const PERIOD_STYLES: Record<string, { emoji: string; bar: string; bg: string; text: string; border: string; dot: string }> = {
  Matin: { emoji: '🌅', bar: 'bg-amber-400', bg: 'bg-amber-50/40', text: 'text-amber-800', border: 'border-amber-200/50', dot: 'bg-amber-400' },
  Midi: { emoji: '☀️', bar: 'bg-orange-400', bg: 'bg-orange-50/40', text: 'text-orange-800', border: 'border-orange-200/50', dot: 'bg-orange-400' },
  Soir: { emoji: '🌙', bar: 'bg-indigo-400', bg: 'bg-indigo-50/40', text: 'text-indigo-800', border: 'border-indigo-200/50', dot: 'bg-indigo-400' },
};

function getTauxBadge(taux: number, inclus: boolean, codeNGAP: string) {
  if (inclus) return { text: 'Inclus', cls: 'bg-foreground-100 text-foreground-400 line-through' };
  if (taux === 0) return { text: 'Gratuit', cls: 'bg-foreground-100 text-foreground-400' };
  if (codeNGAP.includes('AMX')) {
    if (taux >= 50) return { text: '100% AMX', cls: 'bg-emerald-100 text-emerald-700' };
    if (taux >= 25) return { text: '50% AMX', cls: 'bg-amber-100 text-amber-700' };
    return { text: `${taux}%`, cls: 'bg-amber-100 text-amber-700' };
  }
  if (taux >= 100) return { text: '100%', cls: 'bg-emerald-100 text-emerald-700' };
  if (taux >= 50) return { text: '50%', cls: 'bg-amber-100 text-amber-700' };
  return { text: `${taux}%`, cls: 'bg-amber-100 text-amber-700' };
}

export default function BSIResultsDisplay({ bsiResult, explication, regles, mode }: BSIResultsDisplayProps) {
  const [openRule, setOpenRule] = useState<string | null>(null);
  const pourquoi = genererPourquoi(bsiResult);

  const periodesMap: Record<string, typeof bsiResult.actes> = {};
  for (const a of bsiResult.actes) {
    const nom = a.acte.periode === 'matin' ? 'Matin' : a.acte.periode === 'midi' ? 'Midi' : 'Soir';
    if (!periodesMap[nom]) periodesMap[nom] = [];
    periodesMap[nom].push(a);
  }
  const periodeOrder = ['Matin', 'Midi', 'Soir'];
  const sortedPeriodes = periodeOrder.filter((p) => periodesMap[p]);

  const nbActes = bsiResult.actes.length;
  const nbInclus = bsiResult.actes.filter((a) => a.inclusDansForfait).length;
  const nbFacturables = bsiResult.actes.filter((a) => a.facturableEnComplement).length;
  const nbExceptions = bsiResult.actes.filter((a) => a.exceptionAppliquee).length;
  const hasErrors = bsiResult.contradictions.some((c) => c.type === 'error');
  const hasWarnings = bsiResult.contradictions.some((c) => c.type === 'warning');

  return (
    <div className="space-y-5">

      {/* ═══ KPI CARDS ═══ */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="animate-kpi-enter animate-kpi-1">
          <KpiCard
            icon="ri-file-list-3-line"
            value={bsiResult.forfait ? bsiResult.forfait.code : 'Hors BSI'}
            label="Forfait"
            color="primary"
          />
        </div>
        <div className="animate-kpi-enter animate-kpi-2">
          <KpiCard
            icon="ri-money-euro-circle-line"
            value={`${bsiResult.totalJournalier.toFixed(2).replace('.', ',')} €`}
            label="Total estimé"
            color="accent"
            big
          />
        </div>
        <div className="animate-kpi-enter animate-kpi-3">
          <KpiCard
            icon="ri-stethoscope-line"
            value={`${nbFacturables}`}
            label="Actes facturables"
            color="secondary"
          />
        </div>
        <div className="animate-kpi-enter animate-kpi-4">
          <KpiCard
            icon="ri-star-line"
            value={`${nbExceptions}`}
            label="Exceptions"
            color={nbExceptions > 0 ? 'amber' : 'secondary'}
          />
        </div>
      </div>

      {/* ═══ CARTE FORFAIT DÉTAILLÉE ═══ */}
      {bsiResult.forfait && (
        <div className="animate-stagger-child animate-stagger-2 bg-primary-50 border border-primary-200/60 rounded-xl p-5">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-file-list-3-line text-primary-600 text-xl"></i>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-lg font-bold text-primary-800">
                  Forfait {bsiResult.forfait.code}
                </h3>
                <span className="text-xl font-bold text-primary-600">
                  {bsiResult.forfait.montantJournalier.toFixed(2).replace('.', ',')} €
                </span>
              </div>
              <p className="text-xs text-primary-600 mb-3">
                {bsiResult.forfait.label} — Journalier — {bsiResult.forfait.niveauGIR}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {['Toilette', 'Change', 'Nursing', 'Hydratation', 'Alimentation', 'Prévention escarres', 'Mobilisation', 'Admin. traitement'].map((item) => (
                  <span key={item} className="text-[10px] font-medium text-primary-700 bg-primary-100/70 px-2 py-0.5 rounded-full border border-primary-200/40">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>
          {nbInclus > 0 && (
            <div className="mt-3 pt-3 border-t border-primary-200/40 flex items-center gap-2">
              <i className="ri-check-double-line text-primary-500 text-sm"></i>
              <p className="text-xs text-primary-700">
                {nbInclus} acte{nbInclus > 1 ? 's' : ''} inclus{nbInclus > 1 ? 's' : ''} dans le forfait — facturation à 0 €
              </p>
            </div>
          )}
        </div>
      )}

      {/* ═══ TIMELINE DES ACTES PAR PÉRIODE ═══ */}
      <div className="animate-stagger-child animate-stagger-3 bg-white border border-background-200/60 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-background-100 flex items-center gap-2.5">
          <i className="ri-time-line text-primary-500 text-lg"></i>
          <h3 className="text-sm font-bold text-foreground-800">Actes par période</h3>
          <span className="text-xs text-foreground-400 ml-auto">{nbActes} acte{nbActes > 1 ? 's' : ''}</span>
        </div>

        {sortedPeriodes.map((periodeNom) => {
          const acts = periodesMap[periodeNom];
          const style = PERIOD_STYLES[periodeNom];
          const periodTotal = acts.reduce((sum, a) => sum + a.montantFinal, 0);

          return (
            <div key={periodeNom} className={`border-b ${style.border} last:border-b-0`}>
              {/* En-tête période */}
              <div className={`${style.bg} px-5 py-2.5 flex items-center gap-2.5`}>
                <span className="text-base">{style.emoji}</span>
                <span className={`text-sm font-bold ${style.text} flex-1`}>{periodeNom}</span>
                <span className="text-xs font-semibold text-foreground-600">
                  {periodTotal.toFixed(2).replace('.', ',')} €
                </span>
              </div>

              {/* Liste des actes */}
              <div className="divide-y divide-background-100/60">
                {acts.map((a) => {
                  const badge = getTauxBadge(a.tauxApplique, a.inclusDansForfait, a.codeNGAP);
                  const isInclus = a.inclusDansForfait;

                  return (
                    <div
                      key={a.acte.acte.id + a.acte.periode}
                      className={`flex items-start px-5 py-3 transition-colors ${
                        isInclus ? 'bg-foreground-50/30' :
                        a.tauxApplique === 0 ? 'bg-foreground-50/20' :
                        a.tauxApplique <= 50 ? 'bg-amber-50/20' : ''
                      }`}
                    >
                      {/* Ligne verticale période */}
                      <div className={`w-1 self-stretch rounded-full mr-3 flex-shrink-0 ${style.bar} opacity-40`}></div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className={`inline-block px-2 py-0.5 text-xs font-bold rounded ${
                            isInclus ? 'bg-foreground-100 text-foreground-400 line-through' : 'bg-primary-100 text-primary-700'
                          }`}>
                            {a.codeNGAP}
                          </span>
                          <span className={`text-sm font-medium ${isInclus ? 'text-foreground-400 line-through' : 'text-foreground-800'}`}>
                            {a.acte.acte.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap mt-1">
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${badge.cls}`}>
                            {badge.text}
                          </span>
                          {a.exceptionAppliquee && (
                            <span className="text-[10px] font-semibold text-accent-700 bg-accent-50 px-1.5 py-0.5 rounded border border-accent-200/40">
                              {a.exceptionAppliquee}
                            </span>
                          )}
                          {a.raison && (
                            <span className="text-[10px] text-foreground-400 italic">
                              {a.raison}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="text-right flex-shrink-0 ml-3">
                        <span className={`text-base font-bold ${
                          isInclus ? 'text-foreground-300 line-through' :
                          a.tauxApplique === 0 ? 'text-foreground-300' :
                          a.tauxApplique <= 50 ? 'text-amber-700' : 'text-foreground-900'
                        }`}>
                          {a.montantFinal.toFixed(2).replace('.', ',')} €
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Légende */}
        <div className="px-5 py-2.5 bg-background-50 border-t border-background-100 flex items-center gap-4 flex-wrap">
          <span className="text-[10px] text-foreground-400 uppercase tracking-wider">Légende</span>
          <span className="flex items-center gap-1 text-[10px] text-emerald-700">
            <span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-200 inline-block"></span>
            100%
          </span>
          <span className="flex items-center gap-1 text-[10px] text-amber-700">
            <span className="w-3 h-3 rounded bg-amber-100 border border-amber-200 inline-block"></span>
            50%
          </span>
          <span className="flex items-center gap-1 text-[10px] text-foreground-400">
            <span className="w-3 h-3 rounded bg-foreground-100 border border-foreground-200 inline-block"></span>
            Gratuit / Inclus
          </span>
        </div>

        {/* Total */}
        <div className="px-5 py-4 bg-primary-50/30 border-t-2 border-primary-100">
          {/* Décomposition */}
          <div className="space-y-2 mb-3">
            {bsiResult.forfait && (
              <div className="flex items-center justify-between">
                <span className="text-xs text-foreground-600">Forfait {bsiResult.forfait.code}</span>
                <span className="text-xs font-semibold text-foreground-700">{bsiResult.forfait.montantJournalier.toFixed(2).replace('.', ',')} €</span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-xs text-foreground-600">Actes techniques</span>
              <span className="text-xs font-semibold text-foreground-700">{bsiResult.actes.reduce((s, a) => s + a.montantFinal, 0).toFixed(2).replace('.', ',')} €</span>
            </div>
            {bsiResult.ifi.nbVisites > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-xs text-foreground-600">
                  IFI × {bsiResult.ifi.nbVisites} visite{bsiResult.ifi.nbVisites > 1 ? 's' : ''} ({bsiResult.ifi.montantUnitaire.toFixed(2).replace('.', ',')} €/visite)
                </span>
                <span className="text-xs font-semibold text-accent-700">{bsiResult.ifi.totalIFI.toFixed(2).replace('.', ',')} €</span>
              </div>
            )}
            {/* Majorations */}
            {bsiResult.majorations.filter((m) => m.applicable).map((m, i) => (
              <div key={i} className="flex items-center justify-between">
                <span className="text-xs text-foreground-600">{m.nom}</span>
                <span className="text-xs font-semibold text-secondary-700">{m.montant.toFixed(2).replace('.', ',')} €</span>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-primary-200/40">
            <div>
              <span className="text-sm font-bold text-foreground-800">Total journalier estimé</span>
              <p className="text-[10px] text-foreground-400">Forfait + actes + IFI + majorations — Tarifs NGAP 2026</p>
            </div>
            <span className="text-2xl font-bold text-primary-600">
              {bsiResult.totalJournalier.toFixed(2).replace('.', ',')} €
            </span>
          </div>
        </div>
      </div>

      {/* ═══ PANNEAU DE RÈGLES NGAP INTERACTIF ═══ */}
      <NGAPRulesPanel bsiResult={bsiResult} />

      {/* ═══ POURQUOI ═══ */}
      {(mode === 'expliquer' || pourquoi.length > 0) && (
        <div className="animate-stagger-child animate-stagger-4 bg-white border border-background-200/60 rounded-xl p-5">
          <h4 className="text-sm font-bold text-foreground-800 mb-4 flex items-center gap-2">
            <i className="ri-question-line text-accent-500 text-lg"></i>
            POURQUOI cette cotation ?
          </h4>
          <div className="space-y-3">
            {pourquoi.map((ligne, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-[10px] font-bold text-accent-600">{i + 1}</span>
                </div>
                <p className="text-xs text-foreground-700 leading-relaxed pt-1">{ligne}</p>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-foreground-300 mt-4">
            L&apos;IDEL décrit les soins. CotIdel comprend, vérifie, calcule et explique.
          </p>
        </div>
      )}

      {/* ═══ EXPLICATIONS BSI (détaillées) ═══ */}
      {mode === 'expliquer' && explication && (
        <div className="animate-stagger-child animate-stagger-5 bg-white border border-background-200/60 rounded-xl p-5">
          <h4 className="text-sm font-bold text-foreground-800 mb-4 flex items-center gap-2">
            <i className="ri-brain-line text-primary-500 text-lg"></i>
            Raisonnement détaillé
          </h4>

          {explication.resume && (
            <p className="text-xs text-foreground-600 leading-relaxed mb-4 pb-4 border-b border-background-100">
              {explication.resume}
            </p>
          )}

          <div className="space-y-3">
            {explication.periodes.map((periode, pIndex) => (
              <div key={pIndex} className="rounded-lg bg-background-50 border border-background-100 p-3.5">
                <div className="flex items-center gap-2 mb-2.5">
                  <span className="text-sm">{periode.emoji}</span>
                  <span className="text-xs font-bold text-foreground-800">{periode.nom}</span>
                </div>
                <div className="space-y-2">
                  {periode.actes.map((acte, aIndex) => (
                    <div key={aIndex} className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-stethoscope-line text-primary-600 text-xs"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-bold text-foreground-800">{acte.code}</span>
                          <span className="text-xs text-foreground-600">{acte.label}</span>
                          <span className="text-xs font-semibold text-primary-600 ml-auto whitespace-nowrap">{acte.montant}</span>
                        </div>
                        <p className="text-[11px] text-foreground-500 mt-0.5 leading-relaxed">{acte.explication}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {explication.totalMessage && (
            <p className="text-xs font-semibold text-primary-700 mt-4 pt-3 border-t border-primary-100 leading-relaxed">
              {explication.totalMessage}
            </p>
          )}
        </div>
      )}

      {/* ═══ ALERTES ═══ */}
      {bsiResult.contradictions.length > 0 && (
        <div className={`rounded-xl p-5 ${
          hasErrors ? 'bg-red-50 border border-red-200/60' :
          hasWarnings ? 'bg-amber-50 border border-amber-200/60' :
          'bg-blue-50 border border-blue-200/60'
        }`}>
          <h4 className="text-sm font-bold text-foreground-800 mb-3 flex items-center gap-2">
            <i className={`text-lg ${
              hasErrors ? 'ri-error-warning-line text-red-500' :
              hasWarnings ? 'ri-lightbulb-line text-amber-500' :
              'ri-information-line text-blue-500'
            }`}></i>
            {hasErrors ? 'Cotation à corriger' : hasWarnings ? 'Points d\'attention' : 'Informations'}
          </h4>
          <div className="space-y-3">
            {bsiResult.contradictions.map((ctr, i) => (
              <div key={i} className="bg-white rounded-lg p-3.5 border border-background-200/50">
                <div className="flex items-start gap-2.5">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    ctr.type === 'error' ? 'bg-red-100' :
                    ctr.type === 'warning' ? 'bg-amber-100' :
                    'bg-blue-100'
                  }`}>
                    <i className={`text-sm ${
                      ctr.type === 'error' ? 'ri-close-line text-red-600' :
                      ctr.type === 'warning' ? 'ri-error-warning-line text-amber-600' :
                      'ri-information-line text-blue-500'
                    }`}></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-800">{ctr.titre}</p>
                    <p className="text-xs text-foreground-600 mt-0.5 leading-relaxed">{ctr.description}</p>
                    <p className="text-xs text-accent-700 mt-1.5 font-medium">
                      <i className="ri-arrow-right-line text-xs mr-0.5"></i>
                      {ctr.suggestion}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ═══ RÈGLES APPLIQUÉES ═══ */}
      <div className="bg-white border border-background-200/60 rounded-xl p-5">
        <h4 className="text-sm font-bold text-foreground-800 mb-4 flex items-center gap-2">
          <i className="ri-bookmark-line text-secondary-500 text-lg"></i>
          Règles appliquées
        </h4>
        <div className="space-y-2">
          {regles.map((regle, i) => {
            const isOpen = openRule === regle.article;
            return (
              <div key={i} className="border border-background-100 rounded-lg overflow-hidden">
                <button
                  onClick={() => setOpenRule(isOpen ? null : regle.article)}
                  className="w-full flex items-center justify-between px-3.5 py-2.5 hover:bg-background-50 transition-colors cursor-pointer text-left"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <i className={`text-sm flex-shrink-0 transition-transform duration-200 ${isOpen ? 'ri-arrow-down-s-line rotate-180 text-primary-500' : 'ri-arrow-right-s-line text-foreground-400'}`}></i>
                    <span className="text-xs font-bold text-foreground-700">{regle.article}</span>
                    <span className="text-xs text-foreground-500 truncate">{regle.titre}</span>
                  </div>
                </button>
                {isOpen && (
                  <div className="px-3.5 pb-3 pt-1 bg-secondary-50/30">
                    <p className="text-xs text-foreground-700 leading-relaxed mb-2">{regle.texte}</p>
                    <div className="flex items-center gap-4 text-[10px] text-foreground-400">
                      <span><strong className="text-foreground-500">Source :</strong> {regle.source}</span>
                      <span><strong className="text-foreground-500">Vigueur :</strong> {regle.dateEntreeVigueur}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ═══ FOOTER SOURCES ═══ */}
      <div className="text-center pt-2">
        <p className="text-[10px] text-foreground-300">
          Tarifs NGAP 2026 — Métropole. Analyse générée le {new Date().toLocaleDateString('fr-FR')}.
          <br />
          <span className="text-foreground-300/70">Aucune donnée conservée. Vérifiez sur Ameli.fr.</span>
        </p>
      </div>
    </div>
  );
}

// ═══ KPI CARD ═══
function KpiCard({ icon, value, label, color, big }: { icon: string; value: string; label: string; color: string; big?: boolean }) {
  const colorMap: Record<string, { bg: string; icon: string; text: string }> = {
    primary: { bg: 'bg-primary-50', icon: 'text-primary-500', text: 'text-primary-700' },
    accent: { bg: 'bg-accent-50', icon: 'text-accent-500', text: 'text-accent-700' },
    secondary: { bg: 'bg-secondary-50', icon: 'text-secondary-500', text: 'text-secondary-700' },
    amber: { bg: 'bg-amber-50', icon: 'text-amber-500', text: 'text-amber-700' },
  };
  const c = colorMap[color] || colorMap.secondary;

  return (
    <div className={`rounded-xl border border-background-200/60 p-3.5 flex flex-col ${c.bg}`}>
      <div className="flex items-center gap-2 mb-2">
        <i className={`${icon} ${c.icon} text-sm`}></i>
        <span className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider">{label}</span>
      </div>
      <span className={`font-bold ${big ? 'text-xl' : 'text-base'} ${c.text}`}>
        {value}
      </span>
    </div>
  );
}